import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { Flag } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Chip, Typography, Checkbox } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Column } from 'react-table';
import { useQueryParams } from 'use-query-params';

import { fetchVerboseCandidates, patchCandidate } from 'api';
import { LinkButton } from 'components';
import { Table } from 'components/Table';
import { CheckboxWrapper } from 'components/Table/styleComponents';
import {
  convertToDateShort,
  flagTypes,
  getErrorDetail,
  getPageCount,
} from 'helpers';
import {
  DelimitedNumericArrayParam,
  DateParam,
  StringParam,
  NumberParam,
  BooleanParam,
  createEnumArrayParam,
  createEnumParam,
} from 'helpers/useQueryParams';
import { useUrlPagination, useControlledPagination } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import {
  ContractOwner,
  FlagType,
  ReviewedFullCandidateProps,
  ReviewStatus,
} from 'types';

import {
  CellWrapper,
  DashboardWrapper,
  NullValueMarker,
} from '../styledComponents';
import { AddAdminFlag } from './AddAdminFlag';
import { SignerCandidateTableSidebar } from './SignerCandidateTableSidebar';

const filterConfig = {
  organization_id: DelimitedNumericArrayParam,
  tranche_id: DelimitedNumericArrayParam,
  birthdate_min: DateParam,
  birthdate_max: DateParam,
  candidate_id: DelimitedNumericArrayParam,
  last_reviewed_date_min: StringParam,
  last_reviewed_date_max: StringParam,
  fuzzy: BooleanParam,
  review_status_includes: createEnumArrayParam<ReviewStatus>([
    'pending_user_review',
    'review_in_progress',
    'did_not_work_insufficient_info',
    'did_not_work_certain',
    'reviewed_and_verified',
    'unable_to_review',
  ]),
  review_status_only: createEnumParam<ReviewStatus>([
    'pending_user_review',
    'review_in_progress',
    'did_not_work_insufficient_info',
    'did_not_work_certain',
    'reviewed_and_verified',
    'unable_to_review',
  ]),
  contract_owner_includes: createEnumArrayParam<ContractOwner>([
    'dod_contract',
    'not_dod_contract',
    'insufficient_information',
  ]),
  contract_owner_only: createEnumParam<ContractOwner>([
    'dod_contract',
    'not_dod_contract',
    'insufficient_information',
  ]),
  flag_types_include: createEnumArrayParam<FlagType>(flagTypes),
  flag_types_exclude: createEnumArrayParam<FlagType>(flagTypes),
  validated_work_days_min: NumberParam,
  validated_work_days_max: NumberParam,
  has_derogs: BooleanParam,
  has_for_cause: BooleanParam,
  firstname: StringParam,
  lastname: StringParam,
  birthdate: StringParam,
  tazkera: StringParam,
  case_number: StringParam,
  passport_number: StringParam,
};

export const SignerCandidateTable = () => {
  const { setSnack } = useSnackContext();
  const location = useLocation();
  const urlFrom = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search],
  );
  const { controlledRows, controlledPageIndex } = useUrlPagination();
  const [filters, setFilters] = useQueryParams(filterConfig, {
    updateType: 'replaceIn',
  });

  const config = useMemo(() => {
    const { case_number, firstname, lastname, passport_number, ...rest } =
      filters;
    const firstnameTrimmed = firstname?.trim();
    const lastnameTrimmed = lastname?.trim();
    const fuzzy =
      filters.firstname ||
      filters.lastname ||
      filters.case_number ||
      filters.passport_number
        ? true
        : undefined;
    return {
      params: {
        page_number: controlledPageIndex,
        limit: controlledRows,
        firstname: firstnameTrimmed,
        lastname: lastnameTrimmed,
        case_number: case_number,
        passport_number: passport_number,
        ...rest,
        fuzzy,
      },
    };
  }, [controlledPageIndex, controlledRows, filters]);
  const { dataArray, dataLoaded, count, updateData } = useControlledPagination(
    fetchVerboseCandidates,
    config,
  );
  const { updateControlledPageIndex } = useUrlPagination();

  const globalFiltersApplied = useMemo(
    () => Object.entries(filters).some(([, value]) => value !== undefined),
    [filters],
  );

  // update and return to first page when filter is applied
  const wrapSetCandidateFilter = useCallback(
    (arg: Parameters<typeof setFilters>[0]) => {
      setFilters(arg);
      updateControlledPageIndex(1);
    },
    [setFilters, updateControlledPageIndex],
  );

  const [selectAllOffset, setSelectAllOffset] = useState(135);
  const [addFlagModal, setAddFlagModal] = useState(false);
  const [checkedCandidates, setCheckedCandidates] = useState<Set<number>>(
    new Set(),
  );
  useEffect(() => {
    try {
      setCheckedCandidates(
        new Set(JSON.parse(localStorage.getItem('checkedCandidates') ?? '[]')),
      );
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'warning',
        open: true,
        message: 'Failed to parse local storage',
        error: e as Error,
      }));
    }
  }, [setSnack]);
  const tableControls = useMemo(() => {
    return {
      ml: selectAllOffset,
      selectAll: (props: { action: boolean; entities: Array<any> }) => {
        const { action, entities } = props;
        if (action !== null) {
          action
            ? setCheckedCandidates(
                (prev) =>
                  new Set(
                    entities
                      .filter(({ tranche_id }) => !Number.isInteger(tranche_id))
                      .reduce((prev, { id }) => prev.add(id), prev),
                  ),
              )
            : setCheckedCandidates(new Set());
        }
      },
      controls: [
        {
          key: 'add_flag',
          text: 'Add Flag',
          icon: <Flag />,
          disabled: !checkedCandidates.size,
          click: () => setAddFlagModal(true),
        },
      ],
      selected: checkedCandidates,
    };
  }, [setCheckedCandidates, checkedCandidates, selectAllOffset]);

  useLayoutEffect(() => {
    const table = document.querySelector('.table');
    if (table) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const inspect = entry.target.querySelector('.inspect:first-child');
          const checkbox = entry.target.querySelector('.checkbox:first-child');
          if (inspect && checkbox) {
            const inspectRect = inspect.getBoundingClientRect();
            const checkboxRect = checkbox.getBoundingClientRect();
            setSelectAllOffset(checkboxRect.x - inspectRect.x);
          }
        }
      });
      resizeObserver.observe(table);
      return () => resizeObserver.unobserve(table);
    }
  }, [dataArray]);
  useEffect(() => {
    localStorage.setItem(
      'checkedCandidates',
      JSON.stringify(Array.from(checkedCandidates.values())),
    );
    return () => {
      localStorage.setItem('ceckedCandidates', JSON.stringify([]));
    };
  }, [checkedCandidates]);

  const columns = useMemo(
    (): Array<Column<ReviewedFullCandidateProps>> => [
      {
        id: 'inspect',
        Header: () => {
          return (
            <CellWrapper aria-label='Inspect candidate'>
              <p />
            </CellWrapper>
          );
        },
        width: 100,
        Cell: (row: any) => {
          return (
            <CellWrapper className='inspect'>
              <LinkButton
                aria-label={
                  'Inspect ' +
                  row.row.original.firstname +
                  ' ' +
                  row.row.original.lastname
                }
                variant='text'
                sx={{ width: '70%' }}
                to={{
                  pathname: `reviewed_candidates/${row.row.original.id}`,
                  state: { from: urlFrom },
                }}
              >
                INSPECT
              </LinkButton>
            </CellWrapper>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'checked',
        width: 50,
        Cell: (row: any) => {
          const { id, tranche_id } = row.row.original;
          const checked = checkedCandidates.has(id);

          return (
            <CellWrapper>
              <CheckboxWrapper>
                <Checkbox
                  className='checkbox'
                  checked={checked}
                  disabled={Number.isInteger(tranche_id)}
                  size='small'
                  onChange={(e) => {
                    e.preventDefault();
                    setCheckedCandidates(
                      (prev) =>
                        new Set(
                          checked
                            ? (prev.delete(id), prev.values())
                            : prev.add(id),
                        ),
                    );
                  }}
                />
              </CheckboxWrapper>
            </CellWrapper>
          );
        },
      },
      {
        id: 'firstname',
        Header: 'Given Name',
        accessor: 'firstname',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'lastname',
        Header: 'Family Name',
        accessor: 'lastname',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'birthdate',
        Header: 'DOB',
        accessor: (row) => convertToDateShort(row.birthdate),
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'siv_id',
        Header: 'NVCSIV ID',
        accessor: 'case_number',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'passport_number',
        Header: 'Passport',
        accessor: 'passport_number',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'tazkera',
        Header: 'Tazkera',
        accessor: 'tazkera',
        Cell: (row: any) => {
          const { tazkera } = row.row.original;
          return tazkera && tazkera !== 'null' ? tazkera : <NullValueMarker />;
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'employer_group',
        Header: 'Employer Name',
        accessor: 'organizations',
        Cell: (row) => {
          const { organizations } = row.row.original;
          if (organizations.length === 0) {
            return (
              <CellWrapper>
                <NullValueMarker />
              </CellWrapper>
            );
          }
          return (
            <Typography variant='body1'>
              {organizations.map((o) => o.name).join(' // ')}
            </Typography>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'derog',
        Header: 'Derog',
        Cell: (row: any) => {
          const { validated_work_claims } = row.row
            .original as ReviewedFullCandidateProps;
          const hasDerog = validated_work_claims.some(
            (claim) => claim.has_derog,
          );
          return hasDerog ? 'Derog' : <NullValueMarker />;
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'flags',
        Header: 'Flags',
        accessor: 'flags',
        Cell: (row) => {
          const { flags } = row.row.original;

          return flags.length === 0 ? (
            <NullValueMarker />
          ) : (
            flags.map(({ type }, index) => {
              switch (type) {
                case 'contract_date_mismatch':
                  return <Chip key={type} label='Date Mismatch' />;
                case 'work_period_quantity':
                  return <Chip key={type} label='Work Period Quantity' />;
                case 'has_non_dod_work_periods':
                  return <Chip key={type} label='Has Non-DOD Work Periods' />;
                case 'admin_flag':
                  return <Chip key={type} label='Admin' />;
                case 'dod_complete':
                  return <Chip key={type} label='DOD Complete' />;
                case 'derog_flag':
                  return <Chip key={type} label='Derog Flag' />;
                case 'state_action_required':
                  return <Chip key={type} label='State action equired' />;
                case 'unable_to_verify':
                  return <Chip key={type} label='Unable to Verify' />;
                case 'employer_not_in_rabbit':
                  return <Chip key={type} label='Employer is not in Rabbit' />;
                default:
                  return (
                    // NOTE: where type is undefined
                    // Unknown Types have no other unique traits, we need to use index
                    // eslint-disable-next-line react/no-array-index-key
                    <Chip key={`unknown_type_${index}`} label='Unknown Type' />
                  );
              }
            })
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'tranche',
        Header: 'Tranche',
        accessor: 'tranche',
        Cell: (row: any) => {
          const [loading, setLoading] = useState(false);
          const candidate = row.row.original as ReviewedFullCandidateProps;
          const { tranche, id } = candidate;

          switch (tranche?.status) {
            case undefined:
              return <NullValueMarker />;
            case 'sent_to_state':
              return (
                <Typography variant='body1'>
                  {' '}
                  {`tranche ${tranche.id}`}
                </Typography>
              );
            default:
              return (
                <LoadingButton
                  loading={loading}
                  variant='outlined'
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await patchCandidate(id, {
                        data: { tranche_id: 0 },
                      });
                      updateData({ ...candidate, tranche: null });
                    } catch (e) {
                      console.log(e);
                      setSnack((props) => ({
                        ...props,
                        severity: 'error',
                        open: true,
                        message: getErrorDetail({
                          error: e,
                          fallback: 'Failed To Remove Candidate From Tranche',
                        }),
                        error: e as Error,
                      }));
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  {`Remove From Tranche ${tranche?.id}`}
                </LoadingButton>
              );
          }
        },
        disableFilters: true,
        disableSortBy: false,
      },
    ],
    [updateData, setSnack, checkedCandidates, urlFrom],
  );

  return (
    <>
      <DashboardWrapper>
        <>
          <SignerCandidateTableSidebar
            setFilters={wrapSetCandidateFilter}
            filters={filters}
          />

          <Table<ReviewedFullCandidateProps>
            className='table'
            loading={!dataLoaded}
            count={count}
            globalFiltersApplied={globalFiltersApplied}
            handleGlobalFilterClear={() => {
              // Reset filters in history state
              wrapSetCandidateFilter({
                organization_id: undefined,
                tranche_id: undefined,
                birthdate_min: undefined,
                birthdate_max: undefined,
                candidate_id: undefined,
                last_reviewed_date_min: undefined,
                last_reviewed_date_max: undefined,
                fuzzy: undefined,
                review_status_includes: undefined,
                review_status_only: undefined,
                contract_owner_includes: undefined,
                contract_owner_only: undefined,
                flag_types_include: undefined,
                flag_types_exclude: undefined,
                validated_work_days_min: undefined,
                validated_work_days_max: undefined,
                has_derogs: undefined,
                has_for_cause: undefined,
                firstname: undefined,
                lastname: undefined,
                birthdate: undefined,
                tazkera: undefined,
                case_number: undefined,
                passport_number: undefined,
              });
            }}
            useTableOptions={{
              data: dataArray,
              columns: columns,
              manualPagination: true,
              pageCount: getPageCount(count, controlledRows),
              autoResetPage: false,
            }}
            tableControls={
              dataArray.length || tableControls.selected.size
                ? tableControls
                : undefined
            }
            counterClick={() => {
              wrapSetCandidateFilter({
                candidate_id: Array.from(checkedCandidates.values()),
              });
            }}
          />
        </>
      </DashboardWrapper>
      <AddAdminFlag
        open={addFlagModal}
        checkedCandidates={checkedCandidates}
        updateData={updateData}
        handleClose={() => setAddFlagModal(false)}
        setCheckedCandidates={setCheckedCandidates}
      />
    </>
  );
};
